import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Link2, CheckCircle, User, Loader2, X, AlertTriangle, BookOpen, Calendar, Eye, Heart, Clock } from 'lucide-react';
import SystemHeader from '../SystemHeader';
import MonthYearFilter from '../shared/month-year-filter';
import ReelsAutoCheckDetailModal from './reels-auto-check-detail-modal';
import ApprovalStatusBadge from '../shared/approval-status-badge';
import { ReelsAutoCheckAPI } from '../../services/feature-api';
import { markdownToHtml } from '../../utils/markdownToHtml';
import { cdnThumb } from '../../utils/CdnImage';

const ACCENT = '#9C27B0';

// --- Progress messages that rotate during loading ---
const PROGRESS_MESSAGES = [
  { min: 0, max: 25, text: 'Đang phân tích link reel của bạn...', sub: 'Vui lòng chờ trong giây lát' },
  { min: 25, max: 50, text: 'Đang kết nối Facebook để lấy thông tin...', sub: 'Hệ thống đang xử lý' },
  { min: 50, max: 80, text: 'Đang tổng hợp số liệu reel...', sub: 'Lượt xem, thích, bình luận...' },
  { min: 80, max: 100, text: 'Sắp xong rồi, chờ chút nha...', sub: 'Chỉ còn vài giây nữa thôi' },
];

const getProgressMessage = (progress) => {
  const msg = PROGRESS_MESSAGES.find(m => progress >= m.min && progress < m.max);
  return msg || PROGRESS_MESSAGES[PROGRESS_MESSAGES.length - 1];
};

const formatNumber = (n) => {
  if (n == null) return '0';
  if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  return String(n);
};

const formatDuration = (seconds) => {
  if (!seconds) return null;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

// --- Success Celebration Popup ---
const SuccessPopup = ({ reel, onClose }) => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    requestAnimationFrame(() => setShow(true));
    const timer = setTimeout(() => onClose(), 12000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      {/* Backdrop */}
      <div className={`absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${show ? 'opacity-100' : 'opacity-0'}`} />

      {/* Confetti particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {Array.from({ length: 24 }).map((_, i) => (
          <div key={i} className="confetti-particle" style={{
            '--x': `${Math.random() * 100}%`,
            '--delay': `${Math.random() * 2}s`,
            '--duration': `${2 + Math.random() * 3}s`,
            '--color': ['#9C27B0', '#E91E63', '#FF9800', '#4CAF50', '#2196F3', '#FFD700'][i % 6],
            '--size': `${6 + Math.random() * 8}px`,
            '--rotation': `${Math.random() * 360}deg`,
          }} />
        ))}
      </div>

      {/* Card */}
      <div
        className={`relative bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden transition-all duration-500 max-h-[90vh] overflow-y-auto ${show ? 'scale-100 opacity-100 translate-y-0' : 'scale-90 opacity-0 translate-y-8'}`}
        onClick={e => e.stopPropagation()}
      >
        {/* Top gradient banner */}
        <div className="bg-gradient-to-br from-purple-600 via-purple-500 to-pink-500 px-4 pt-5 pb-4 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: 'radial-gradient(circle at 30% 50%, white 1px, transparent 1px), radial-gradient(circle at 70% 30%, white 1px, transparent 1px)',
            backgroundSize: '30px 30px, 20px 20px',
          }} />
          <div className="text-6xl mb-3 animate-bounce-slow">🎉</div>
          <h2 className="text-3xl font-black text-white tracking-wide" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.2)' }}>CHÚC MỪNG BẠN!</h2>
          <p className="text-purple-100 text-base mt-1.5 font-semibold">Reel đã được ghi nhận thành công</p>
        </div>

        {/* Close button */}
        <button onClick={onClose} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors">
          <X size={14} className="text-white" />
        </button>

        {reel && (
          <div className="px-4 pt-4 pb-2">
            {/* Thumbnail + Author */}
            <div className="flex gap-3 items-start mb-3">
              {reel.thumbnailUrl ? (
                <img src={cdnThumb(reel.thumbnailUrl)} alt="" loading="lazy" className="w-20 h-20 rounded-xl object-cover flex-shrink-0 shadow-md" />
              ) : (
                <div className="w-20 h-20 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0">
                  <span className="text-3xl">🎬</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                {reel.facebookAuthorName && (
                  <div className="flex items-center gap-1.5 mb-1">
                    <User size={12} className="text-purple-600 flex-shrink-0" />
                    <span className="text-xs font-bold text-gray-800">{reel.facebookAuthorName}</span>
                  </div>
                )}
                {reel.time && (
                  <div className="flex items-center gap-1 text-sm font-bold mb-1" style={{ color: ACCENT }}>
                    <Calendar size={13} />
                    {new Date(reel.time).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    <span className="text-[10px] text-gray-400 font-normal ml-1">
                      {new Date(reel.time).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                )}
                {reel.duration > 0 && (
                  <div className="flex items-center gap-1 text-[11px] text-gray-500">
                    <Clock size={10} />
                    <span>Thời lượng: {formatDuration(reel.duration)}</span>
                  </div>
                )}
                {reel.privacyValue && (
                  <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">
                    {reel.privacyValue === 'EVERYONE' ? '🌐 Công khai' : reel.privacyValue === 'FRIENDS' ? '👥 Bạn bè' : reel.privacyValue}
                  </span>
                )}
              </div>
            </div>

            {/* Stats grid — 1 hàng */}
            <div className="grid grid-cols-3 gap-2 mb-1">
              <div className="bg-blue-50 rounded-xl px-2 py-2 text-center">
                <Eye size={14} className="text-blue-500 mx-auto mb-0.5" />
                <p className="text-sm font-black text-blue-700">{formatNumber(reel.views)}</p>
                <p className="text-[9px] text-blue-400">Lượt xem</p>
              </div>
              <div className="bg-purple-50 rounded-xl px-2 py-2 text-center">
                <span className="text-sm block mb-0.5">❤️🩷😂</span>
                <p className="text-sm font-black text-purple-700">{formatNumber(reel.reactionsTotal)}</p>
                <p className="text-[9px] text-purple-400 font-semibold">Tương tác</p>
              </div>
              <div className="bg-pink-50 rounded-xl px-2 py-2 text-center">
                <Heart size={14} className="text-pink-500 mx-auto mb-0.5" />
                <p className="text-sm font-black text-pink-700">{formatNumber(reel.likes)}</p>
                <p className="text-[9px] text-pink-400">Lượt thích</p>
              </div>
            </div>
            <p className="text-[9px] text-gray-400 text-center mb-3">* Tương tác gồm: ❤️ 🩷 😂 😢 😡 👍 và các cảm xúc khác</p>

            {/* Caption */}
            {reel.text && (
              <div className="bg-gray-50 rounded-xl px-3 py-2 mb-3">
                <p className="text-[11px] text-gray-600 leading-relaxed line-clamp-3">{reel.text}</p>
              </div>
            )}

            {/* Top comments */}
            {reel.topComments && reel.topComments.length > 0 && (
              <div className="mb-3">
                <p className="text-[10px] font-bold text-gray-500 mb-1.5 uppercase tracking-wider">Bình luận tiêu biểu</p>
                <div className="space-y-1">
                  {reel.topComments.slice(0, 5).map((c, i) => (
                    <div key={i} className="bg-gray-50 rounded-lg px-2.5 py-1.5">
                      <span className="text-[10px] font-bold text-gray-700">{c.from?.name || 'Ẩn danh'}: </span>
                      <span className="text-[10px] text-gray-600">{c.message}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Month warning */}
        {reel?.warning && (
          <div className="mx-4 mb-2 rounded-xl px-3 py-2.5 border border-amber-200 bg-amber-50">
            <p className="text-[11px] text-amber-800 leading-relaxed font-medium">⚠️ {reel.warning}</p>
          </div>
        )}

        {/* Motivational message */}
        <div className="mx-4 mb-3 rounded-2xl overflow-hidden" style={{ background: 'linear-gradient(135deg, #f3e8ff 0%, #fce7f3 100%)', border: '1.5px solid #e9d5ff' }}>
          <div className="px-4 py-3 text-center">
            <p className="text-xl font-black leading-snug" style={{ color: ACCENT }}>💜 Hãy cố gắng</p>
            <p className="text-xl font-black leading-snug mb-1" style={{ color: ACCENT }}>chăm chỉ mỗi ngày!</p>
            <p className="text-[12px] text-purple-600 font-medium leading-relaxed">
              <span className="font-bold text-purple-800">Công ty ghi nhận sự nỗ lực của bạn</span> 🌟
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 pb-4">
          <button
            onClick={onClose}
            className="w-full py-2.5 rounded-xl text-white text-sm font-bold transition-all active:scale-95"
            style={{ backgroundColor: ACCENT }}
          >
            Xem danh sách Reels hàng ngày
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Báo lỗi gửi reel (mọi lỗi KHÔNG PHẢI trùng reel) ---
// VÌ SAO CÓ: trước đây chỉ lỗi "trùng reel" mới được hiện. Mọi lỗi khác — link chia sẻ
// không đọc được, Facebook bận, mất mạng giữa chừng — đều bị nuốt im lặng: đại lý bấm
// Gửi, thanh chạy xong, rồi KHÔNG có gì xảy ra. Nhìn y như app hỏng.
const SubmitError = ({ message, onDismiss }) => (
  <div className="mt-2 rounded-xl overflow-hidden border border-red-200 bg-red-50 animate-shake">
    <div className="px-3 py-2.5 flex items-start gap-2.5">
      <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
        <AlertTriangle size={16} className="text-red-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-bold text-red-800">Chưa gửi được reel</p>
        <p className="text-[11px] text-red-700 mt-0.5 leading-relaxed">{message}</p>
      </div>
      <button onClick={onDismiss} className="flex-shrink-0 mt-0.5">
        <X size={14} className="text-red-400 hover:text-red-600" />
      </button>
    </div>
  </div>
);

// --- Duplicate Warning Component ---
const DuplicateWarning = ({ message, onDismiss }) => (
  <div className="mt-2 rounded-xl overflow-hidden border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 animate-shake">
    <div className="px-3 py-2.5 flex items-start gap-2.5">
      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
        <AlertTriangle size={16} className="text-amber-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-bold text-amber-800">Phát hiện trùng reel!</p>
        <p className="text-[11px] text-amber-700 mt-0.5 leading-relaxed">{message}</p>
      </div>
      <button onClick={onDismiss} className="flex-shrink-0 mt-0.5">
        <X size={14} className="text-amber-400 hover:text-amber-600" />
      </button>
    </div>
  </div>
);

const ReelsAutoCheckList = ({ userInfo, accessToken, onLogout, totalPoints, onRefreshPoints }) => {
  const navigate = useNavigate();
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(String(now.getMonth() + 1).padStart(2, '0'));
  const [selectedYear, setSelectedYear] = useState(String(now.getFullYear()));
  // Guide state
  const [showGuide, setShowGuide] = useState(false);
  const [guideContent, setGuideContent] = useState('');

  // Profile state (read-only, auto-populated from first reel)
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);

  // Reel submission state
  const [reelUrl, setReelUrl] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isDuplicateError, setIsDuplicateError] = useState(false);

  // Progress animation state
  const [progress, setProgress] = useState(0);
  const progressTimerRef = useRef(null);

  // Success popup state
  const [successReel, setSuccessReel] = useState(null);

  // List state
  const [reels, setReels] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Detail modal
  const [selectedReel, setSelectedReel] = useState(null);

  // --- Progress animation effect ---
  useEffect(() => {
    if (submitLoading) {
      setProgress(0);
      const startTime = Date.now();
      const maxDuration = 25000; // 25 seconds to reach ~95%

      progressTimerRef.current = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const t = Math.min(elapsed / maxDuration, 1);
        const eased = 1 - Math.pow(1 - t, 3);
        setProgress(Math.min(eased * 95, 95));
      }, 80);
    } else {
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }
    }
    return () => {
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    };
  }, [submitLoading]);

  // Load profile (read-only)
  const loadProfile = useCallback(async () => {
    if (!accessToken) return;
    setProfileLoading(true);
    try {
      const result = await ReelsAutoCheckAPI.getProfile(accessToken);
      if (result.success) setProfile(result.data);
    } catch (e) { console.warn('Failed to load profile:', e); }
    setProfileLoading(false);
  }, [accessToken]);

  // Load reels list
  const loadReels = useCallback(async () => {
    if (!accessToken) return;
    setListLoading(true);
    try {
      const result = await ReelsAutoCheckAPI.getList(accessToken, {
        month: selectedMonth,
        year: selectedYear,
      });
      if (result.success) {
        const items = result.data?.rows || result.data?.items || [];
        const reelsList = Array.isArray(items) ? items : [];
        setReels(reelsList);
        setTotalCount(result.data?.count || reelsList.length);
      }
    } catch (e) { console.warn('Failed to load reels:', e); }
    setListLoading(false);
  }, [accessToken, selectedMonth, selectedYear]);

  useEffect(() => { loadProfile(); }, [loadProfile]);
  useEffect(() => { loadReels(); }, [loadReels]);

  // Submit reel
  const handleSubmitReel = async () => {
    if (!reelUrl.trim()) return;
    setSubmitLoading(true);
    setSubmitError('');
    setIsDuplicateError(false);
    setSuccessReel(null);
    try {
      const result = await ReelsAutoCheckAPI.submitReel(accessToken, reelUrl.trim());
      if (result.success) {
        setProgress(100);
        await new Promise(r => setTimeout(r, 600));
        setSubmitLoading(false);
        setSuccessReel(result.data);
        setReelUrl('');
        loadReels();
        // Reload profile in case this was the first reel (auto-populated FB profile)
        loadProfile();
      } else {
        setSubmitLoading(false);
        const errorMsg = result.error || 'Không thể gửi reel';
        const isDuplicate = errorMsg.includes('đã được gửi');
        setIsDuplicateError(isDuplicate);
        setSubmitError(errorMsg);
      }
    } catch (e) {
      setSubmitLoading(false);
      const errorMsg = e.message || 'Lỗi kết nối';
      const isDuplicate = errorMsg.includes('đã được gửi');
      setIsDuplicateError(isDuplicate);
      setSubmitError(errorMsg);
    }
  };

  const currentMessage = getProgressMessage(progress);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
        @keyframes confetti-fall {
          0% { transform: translateY(-10vh) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) rotate(var(--rotation, 720deg)); opacity: 0; }
        }
        .confetti-particle {
          position: absolute;
          left: var(--x);
          top: -20px;
          width: var(--size);
          height: var(--size);
          background: var(--color);
          border-radius: 2px;
          animation: confetti-fall var(--duration) ease-in var(--delay) forwards;
        }
        .confetti-particle:nth-child(odd) { border-radius: 50%; }
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        .animate-bounce-slow { animation: bounce-slow 2s ease-in-out infinite; }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
          20%, 40%, 60%, 80% { transform: translateX(2px); }
        }
        .animate-shake { animation: shake 0.5s ease-in-out; }
        @keyframes pulse-ring {
          0% { transform: scale(1); opacity: 0.5; }
          100% { transform: scale(1.8); opacity: 0; }
        }
        .pulse-ring { animation: pulse-ring 1.5s ease-out infinite; }
        @keyframes progress-glow {
          0%, 100% { box-shadow: 0 0 4px rgba(156, 39, 176, 0.3); }
          50% { box-shadow: 0 0 12px rgba(156, 39, 176, 0.6); }
        }
        .progress-glow { animation: progress-glow 2s ease-in-out infinite; }
      `}</style>
      <SystemHeader userInfo={userInfo} onLogout={onLogout} totalPoints={totalPoints} pageTitle="Reels hàng ngày" onRefreshPoints={onRefreshPoints} />

      <div className="flex-1 max-w-2xl w-full mx-auto flex flex-col">
        {/* Back + Title + Total — row 1 */}
        <div className="flex items-center gap-2 px-3 py-1.5 bg-white">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium flex-shrink-0" style={{ color: ACCENT, background: ACCENT + '12' }}>
            <ArrowLeft size={13} /> Quay lại
          </button>
          <h1 className="text-xs font-bold flex-1" style={{ color: ACCENT }}>Reels hàng ngày</h1>
          <button onClick={async () => {
              setShowGuide(true);
              if (!guideContent) {
                try {
                  const res = await fetch('/kienthuc/huong-dan-reels-auto-check.md');
                  if (res.ok) {
                    const text = await res.text();
                    if (text.trimStart().startsWith('<!') || text.trimStart().startsWith('<html')) {
                      setGuideContent('Không tìm thấy nội dung hướng dẫn.');
                    } else {
                      setGuideContent(text);
                    }
                  } else {
                    setGuideContent('Không tìm thấy nội dung.');
                  }
                } catch { setGuideContent('Lỗi tải hướng dẫn.'); }
              }
            }}
            className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-colors flex-shrink-0"
            style={{ color: '#f59e0b', background: '#fef3c7' }}>
            <BookOpen size={12} />
            <span>Hướng dẫn</span>
          </button>
          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold text-white flex-shrink-0" style={{ backgroundColor: ACCENT }}>Tổng: {totalCount}</span>
        </div>
        {/* Filter — row 2, full width */}
        <div className="px-3 py-1 bg-white border-b border-gray-200">
          <div className="w-full">
            <MonthYearFilter
              selectedMonth={selectedMonth} selectedYear={selectedYear}
              onMonthChange={(m) => setSelectedMonth(m)}
              onYearChange={(y) => setSelectedYear(y)}
            />
          </div>
        </div>

        {/* Profile Section (read-only, auto from first reel) */}
        <div className="mx-3 mt-1.5">
          {profileLoading ? (
            <div className="bg-white rounded-lg p-2 flex items-center justify-center">
              <Loader2 size={16} className="animate-spin text-gray-400" />
            </div>
          ) : profile?.isSetup ? (
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg px-3 py-1.5 border border-purple-200 flex items-center gap-2">
              <CheckCircle size={14} className="text-purple-600 flex-shrink-0" />
              <span className="text-xs text-purple-600">Facebook:</span>
              <span className="text-xs font-bold text-purple-900">{profile.facebookUsername01 || `ID: ${profile.facebookUserId}`}</span>
            </div>
          ) : (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg px-3 py-1.5 border border-blue-200 flex items-center gap-2">
              <User size={14} className="text-blue-500 flex-shrink-0" />
              <span className="text-[11px] text-blue-700">Gửi reel đầu tiên để tự động liên kết Facebook</span>
            </div>
          )}
        </div>

        {/* Reel Submission Section (always visible) */}
        <div className="mx-3 mt-2">
          <div className="bg-white rounded-xl p-3 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <Link2 size={16} style={{ color: ACCENT }} />
              <p className="text-sm font-bold text-gray-800">Gửi Reel mới</p>
            </div>
            <div className="flex gap-2">
              <input
                type="url"
                value={reelUrl}
                onChange={(e) => { setReelUrl(e.target.value); setSubmitError(''); setIsDuplicateError(false); }}
                placeholder="Dán link Facebook Reel..."
                className="flex-1 px-3 py-2 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-purple-300 focus:border-purple-400 outline-none"
                disabled={submitLoading}
              />
              <button
                onClick={handleSubmitReel}
                disabled={submitLoading || !reelUrl.trim()}
                className="px-4 py-2 rounded-xl text-white text-sm font-bold disabled:opacity-40 flex items-center gap-1"
                style={{ backgroundColor: ACCENT }}
              >
                {submitLoading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Gửi
              </button>
            </div>

            {/* Enhanced Loading Animation */}
            {submitLoading && (
              <div className="mt-3 rounded-xl overflow-hidden border border-purple-100 bg-gradient-to-br from-purple-50 via-white to-pink-50">
                {/* Progress bar */}
                <div className="h-2 w-full overflow-hidden bg-purple-100/50 relative">
                  <div
                    className="h-full rounded-full progress-glow relative"
                    style={{
                      backgroundColor: ACCENT,
                      width: `${progress}%`,
                      transition: 'width 0.3s ease-out',
                    }}
                  >
                    <div className="absolute inset-0 overflow-hidden rounded-full">
                      <div className="h-full w-1/3 rounded-full" style={{
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                        animation: 'shimmer 1.5s ease-in-out infinite',
                      }} />
                    </div>
                  </div>
                </div>

                {/* Content area */}
                <div className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="relative flex-shrink-0">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg">
                        <span className="text-lg">👩‍💼</span>
                      </div>
                      <div className="absolute inset-0 rounded-full border-2 border-purple-400 pulse-ring" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-800 leading-tight">{currentMessage.text}</p>
                      <p className="text-[11px] text-gray-400 mt-0.5">{currentMessage.sub}</p>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <span className="text-lg font-black" style={{ color: ACCENT }}>{Math.round(progress)}%</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Trùng reel — cảnh báo vàng, không phải lỗi của đại lý */}
            {submitError && isDuplicateError && (
              <DuplicateWarning
                message={submitError}
                onDismiss={() => { setSubmitError(''); setIsDuplicateError(false); }}
              />
            )}

            {/* Mọi lỗi còn lại. Thiếu nhánh này là đại lý bấm Gửi rồi không thấy gì. */}
            {submitError && !isDuplicateError && (
              <SubmitError
                message={submitError}
                onDismiss={() => setSubmitError('')}
              />
            )}
          </div>
        </div>

        {/* Reels List */}
        <div className="flex-1 overflow-y-auto pb-8 mt-2">
          {listLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 size={24} className="animate-spin text-gray-400" />
            </div>
          ) : reels.length === 0 ? (
            <div className="text-center py-12 text-gray-500 text-sm">Chưa có reel nào.</div>
          ) : (
            <div className="px-3 space-y-1.5">
              {reels.map((reel, index) => (
                <div
                  key={reel.id}
                  className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => setSelectedReel(reel)}
                >
                  <div className="flex">
                    {/* STT + Thumbnail */}
                    <div className="flex-shrink-0 relative">
                      {reel.thumbnailUrl ? (
                        <div className="w-16 h-16">
                          <img src={cdnThumb(reel.thumbnailUrl)} alt="" loading="lazy" className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <div className="w-16 h-16 bg-purple-50 flex items-center justify-center">
                          <span className="text-2xl">🎬</span>
                        </div>
                      )}
                      <span className="absolute top-0 left-0 bg-black/50 text-white text-[9px] font-bold px-1 rounded-br">{index + 1}</span>
                    </div>

                    {/* Content */}
                    <div className="flex-1 py-1.5 px-2.5 min-w-0">
                      {/* Date — main info */}
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <Calendar size={10} style={{ color: ACCENT }} className="flex-shrink-0" />
                        <span className="text-[11px] font-bold" style={{ color: ACCENT }}>
                          {reel.time
                            ? new Date(reel.time).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' })
                            : '—'}
                        </span>
                        {reel.time && (
                          <span className="text-[10px] text-gray-400">
                            {new Date(reel.time).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                        <span className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded bg-purple-100 text-purple-600 font-mono">#{reel.id}</span>
                      </div>

                      {/* Stats mini row */}
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-[9px] text-gray-400 flex items-center gap-0.5">
                          <Eye size={8} /> {formatNumber(reel.views)}
                        </span>
                        <span className="text-[9px] text-gray-400 flex items-center gap-0.5">
                          <Heart size={8} /> {formatNumber(reel.likes)}
                        </span>
                        {reel.duration > 0 && (
                          <span className="text-[9px] text-gray-400 flex items-center gap-0.5">
                            <Clock size={8} /> {formatDuration(reel.duration)}
                          </span>
                        )}
                      </div>

                      {/* Approval status badge */}
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <ApprovalStatusBadge status={reel.companyApproved || 'pending'} />
                        {reel.companyApproved === 'rejected' && reel.notes && (
                          <span className="text-[8px] text-red-500 truncate max-w-[120px]">💬 {reel.notes}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {selectedReel && (
        <ReelsAutoCheckDetailModal
          reel={selectedReel}
          onClose={() => setSelectedReel(null)}
        />
      )}

      {/* Success Celebration Popup */}
      {successReel && (
        <SuccessPopup
          reel={successReel}
          onClose={() => setSuccessReel(null)}
        />
      )}

      {/* Guide popup */}
      {showGuide && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-fade-in" onClick={() => setShowGuide(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col animate-slide-up" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-bold text-gray-800">📖 Hướng dẫn</h3>
              <button onClick={() => setShowGuide(false)} className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200"><X size={14} /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3 text-[13px] text-gray-700 leading-normal" dangerouslySetInnerHTML={{ __html: guideContent ? markdownToHtml(guideContent) : 'Đang tải...' }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ReelsAutoCheckList;
